import { useContext, useEffect, useRef, type ContextType } from 'react';
import { UNSAFE_NavigationContext, useNavigate, type To } from 'react-router-dom';

type Navigation = NonNullable<ContextType<typeof UNSAFE_NavigationContext>>['navigator'];
type Guard = { current: boolean; blocked: boolean };
const registrations = new WeakMap<Navigation, { guards: Set<Guard>; allow: () => boolean; bypass: (run: () => void) => void; dispose: () => void }>();

/** Stage private form data only after every mounted editor agrees to navigation. */
export function useGuardedNavigate() {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const navigate = useNavigate();
  return (to: To, beforeNavigate?: () => boolean) => {
    const entry = registrations.get(navigator);
    if (entry && !entry.allow()) return false;
    if (beforeNavigate && !beforeNavigate()) return false;
    if (entry) entry.bypass(() => navigate(to));
    else navigate(to);
    return true;
  };
}

/** One router guard coordinates all editors, including legacy response sections. */
export function useDraftNavigationGuard(dirty: boolean, blocked = false) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const pending = useRef<Guard>({ current: dirty, blocked });
  pending.current.current = dirty;
  pending.current.blocked = blocked;
  useEffect(() => {
    const existing = registrations.get(navigator);
    if (existing) {
      existing.guards.add(pending.current);
      return () => { existing.guards.delete(pending.current); if (!existing.guards.size) { existing.dispose(); registrations.delete(navigator); } };
    }
    const guards = new Set<Guard>([pending.current]);
    const hasChanges = () => [...guards].some(g => g.current);
    let bypass = false;
    const isBlocked = () => [...guards].some(g => g.blocked);
    const allow = () => bypass || (!isBlocked() && (!hasChanges() || window.confirm('Leave this screen and discard unsaved changes?')));
    const push = navigator.push, replace = navigator.replace, go = navigator.go;
    let index: number | undefined = typeof window.history.state?.idx === 'number' ? window.history.state.idx : undefined;
    let restoring = false, authorizedPop = false;
    const rememberIndex = () => { index = typeof window.history.state?.idx === 'number' ? window.history.state.idx : undefined; };
    navigator.push = (...args) => { if (allow()) { push.apply(navigator, args); rememberIndex(); } };
    navigator.replace = (...args) => { if (allow()) { replace.apply(navigator, args); rememberIndex(); } };
    navigator.go = (...args) => { if (allow()) { authorizedPop = true; go.apply(navigator, args); } };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChanges() || isBlocked()) { event.preventDefault(); event.returnValue = ''; }
    };
    const beforeScope = (event: Event) => {
      if (isBlocked() || (hasChanges() && !window.confirm('Switch entities and discard unsaved changes?'))) event.preventDefault();
    };
    const pop = (event: PopStateEvent) => {
      const next: number | undefined = typeof event.state?.idx === 'number' ? event.state.idx : undefined;
      if (restoring) { restoring = false; event.stopImmediatePropagation(); return; }
      if (authorizedPop) { authorizedPop = false; index = next; return; }
      if ((hasChanges() || isBlocked()) && index !== undefined && next !== undefined && next !== index && !allow()) {
        event.stopImmediatePropagation(); restoring = true; window.history.go(index - next); return;
      }
      index = next;
    };
    const externalLink = (event: MouseEvent) => {
      if ((!hasChanges() && !isBlocked()) || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a') : null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin && !allow()) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('cdf:before-scope-change', beforeScope);
    window.addEventListener('popstate', pop, true);
    document.addEventListener('click', externalLink, true);
    const dispose = () => {
      navigator.push = push; navigator.replace = replace; navigator.go = go;
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('cdf:before-scope-change', beforeScope);
      window.removeEventListener('popstate', pop, true);
      document.removeEventListener('click', externalLink, true);
    };
    registrations.set(navigator, { guards, allow, bypass: run => { bypass = true; try { run(); } finally { bypass = false; } }, dispose });
    return () => { guards.delete(pending.current); if (!guards.size) { dispose(); registrations.delete(navigator); } };
  }, [navigator]);
}
